import { Direction } from '@lyra/core/api/types/private.get_quotes'
import { LegPricedSchema } from '@lyra/core/api/types/private.send_quote'
import BodyText from '@lyra/core/components/BodyText'
import Button from '@lyra/core/components/Button'
import Table, { createColumnHelper } from '@lyra/core/components/Table'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatRelativeDateTime from '@lyra/core/utils/formatRelativeDateTime'
import formatUSD from '@lyra/core/utils/formatUSD'
import { QueryParam } from '@lyra/web/constants/query'
import useRfqSentQuotes from '@lyra/web/hooks/useRfqSentQuotes'
import useSubaccount from '@lyra/web/src/hooks/useSubaccount'
import { formatInstrument, formatInstrumentExpiry } from '@lyra/web/utils/instruments'
import { getLegsPricedTotalCost, getQuoteRfqSize } from '@lyra/web/utils/rfqs'
import { parseAsString, useQueryState } from 'next-usequerystate'
import React, { useMemo } from 'react'
import { StackProps, XStack, YStack } from 'tamagui'

type Props = {
  pageSize?: number
} & StackProps

type QuotesTableData = {
  quoteId: string
  status: string
  legs: LegPricedSchema[]
  size: number
  lastUpdateTimestamp: number
  direction: Direction
  onPress: () => void
}

function formatQuoteId(quoteId: string): string {
  const length = quoteId.length
  return `${quoteId.substring(0, 5)}...${quoteId.substring(length - 5, length)}`
}

const QuotesTable = ({ pageSize, ...tableProps }: Props): JSX.Element => {
  const { subaccount, cancelRfqQuote } = useSubaccount()

  const { data: quotes, mutate: mutateQuotes } = useRfqSentQuotes()

  const columnHelper = createColumnHelper<QuotesTableData>()

  const [_0, setRfqId] = useQueryState(QueryParam.RfqId, parseAsString)

  const rows: QuotesTableData[] = useMemo(() => {
    return quotes.map((quote) => {
      return {
        quoteId: quote.quote_id,
        status: quote.status,
        legs: quote.legs,
        size: getQuoteRfqSize(quote),
        direction: quote.direction,
        lastUpdateTimestamp: +quote.last_update_timestamp,
        onPress: () => {
          setRfqId(quote.rfq_id)
        },
      }
    })
  }, [quotes, setRfqId])

  const columns = useMemo(
    () =>
      filterNulls([
        columnHelper.accessor('status', {
          id: 'status',
          header: 'Status',
          meta: {
            minWidth: 100,
          },
          cell: (props) => (
            <YStack>
              <BodyText>{props.row.original.status.toUpperCase()}</BodyText>
              <BodyText color="secondary">
                {formatRelativeDateTime(new Date(props.row.original.lastUpdateTimestamp))}
              </BodyText>
            </YStack>
          ),
        }),
        columnHelper.accessor('cost', {
          id: 'cost',
          header: 'Cost',
          meta: {
            minWidth: 100,
          },
          cell: (props) => {
            const cost = getLegsPricedTotalCost(props.row.original.legs)
            return <BodyText color={cost < 0 ? 'red' : 'green'}>{formatUSD(cost)}</BodyText>
          },
        }),
        columnHelper.accessor('direction', {
          id: 'direction',
          header: 'Direction',
          meta: {
            minWidth: 70,
            maxWidth: 90,
          },
          cell: (props) => {
            return (
              <BodyText color={props.row.original.direction === 'buy' ? 'red' : 'green'}>
                {props.row.original.direction === 'buy' ? 'Buy' : 'Sell'}
              </BodyText>
            )
          },
        }),
        columnHelper.accessor('size', {
          id: 'size',
          header: 'Size',
          meta: {
            minWidth: 80,
            maxWidth: 90,
          },
          cell: (props) => (
            <BodyText>{formatNumber(props.row.original.size, { minDps: 1 })}</BodyText>
          ),
        }),
        columnHelper.accessor('legOne', {
          id: 'legOne',
          header: 'Leg One',
          meta: {
            minWidth: 200,
          },
          cell: (props) => {
            const leg = props.row.original.legs[0]
            return (
              <YStack>
                <XStack key={leg.instrument_name}>
                  <BodyText>
                    {formatNumber(+leg.amount / props.row.original.size, { minDps: 1 })}
                  </BodyText>
                  <BodyText>{` ${formatInstrument(leg.instrument_name)}`}</BodyText>
                </XStack>
                <XStack>
                  <BodyText color={leg.direction === 'buy' ? 'green' : 'red'}>
                    {leg.direction === 'buy' ? 'Long ' : 'Short '}
                  </BodyText>
                  <BodyText color="secondary">
                    {formatInstrumentExpiry(leg.instrument_name)}
                  </BodyText>
                </XStack>
              </YStack>
            )
          },
        }),
        columnHelper.accessor('legTwo', {
          id: 'legTwo',
          header: 'Leg Two',
          meta: {
            minWidth: 200,
            maxWidth: 200,
          },
          cell: (props) => {
            if (props.row.original.legs.length < 2) {
              return <BodyText>-</BodyText>
            }
            const leg = props.row.original.legs[1]
            return (
              <YStack>
                <XStack key={leg.instrument_name}>
                  <BodyText>
                    {formatNumber(+leg.amount / props.row.original.size, { minDps: 1 })}
                  </BodyText>
                  <BodyText>{` ${formatInstrument(leg.instrument_name)}`}</BodyText>
                </XStack>
                <XStack>
                  <BodyText color={leg.direction === 'buy' ? 'green' : 'red'}>
                    {leg.direction === 'buy' ? 'Long ' : 'Short '}
                  </BodyText>
                  <BodyText color="secondary">
                    {formatInstrumentExpiry(leg.instrument_name)}
                  </BodyText>
                </XStack>
              </YStack>
            )
          },
        }),
        columnHelper.accessor('legThree', {
          id: 'legThree',
          header: 'Leg Three',
          meta: {
            minWidth: 200,
            maxWidth: 200,
          },
          cell: (props) => {
            if (props.row.original.legs.length < 3) {
              return <BodyText>-</BodyText>
            }
            const leg = props.row.original.legs[2]
            return (
              <YStack>
                <XStack key={leg.instrument_name}>
                  <BodyText>
                    {formatNumber(+leg.amount / props.row.original.size, { minDps: 1 })}
                  </BodyText>
                  <BodyText>{` ${formatInstrument(leg.instrument_name)}`}</BodyText>
                </XStack>
                <XStack>
                  <BodyText color={leg.direction === 'buy' ? 'green' : 'red'}>
                    {leg.direction === 'buy' ? 'Long ' : 'Short '}
                  </BodyText>
                  <BodyText color="secondary">
                    {formatInstrumentExpiry(leg.instrument_name)}
                  </BodyText>
                </XStack>
              </YStack>
            )
          },
        }),
        columnHelper.accessor('legFour', {
          id: 'legFour',
          header: 'Leg Four',
          meta: {
            minWidth: 200,
            maxWidth: 200,
          },
          cell: (props) => {
            if (props.row.original.legs.length < 4) {
              return <BodyText>-</BodyText>
            }
            const leg = props.row.original.legs[3]
            return (
              <YStack>
                <XStack key={leg.instrument_name}>
                  <BodyText>
                    {formatNumber(+leg.amount / props.row.original.size, { minDps: 1 })}
                  </BodyText>
                  <BodyText>{` ${formatInstrument(leg.instrument_name)}`}</BodyText>
                </XStack>
                <XStack>
                  <BodyText color={leg.direction === 'buy' ? 'green' : 'red'}>
                    {leg.direction === 'buy' ? 'Long ' : 'Short '}
                  </BodyText>
                  <BodyText color="secondary">
                    {formatInstrumentExpiry(leg.instrument_name)}
                  </BodyText>
                </XStack>
              </YStack>
            )
          },
        }),
        columnHelper.accessor('action', {
          id: 'action',
          header: 'Action',
          meta: {
            minWidth: 165,
          },
          cell: (props) =>
            props.row.original.status === 'open' ? (
              <Button
                label="Cancel"
                onPress={async () => {
                  if (!subaccount) {
                    return
                  }
                  try {
                    await cancelRfqQuote({
                      subaccount_id: subaccount.subaccount_id,
                      quote_id: props.row.original.quoteId,
                    })
                    await mutateQuotes()
                  } catch (error) {
                    console.error(error)
                  }
                }}
              />
            ) : (
              <BodyText>-</BodyText>
            ),
        }),
        columnHelper.accessor('quoteId', {
          id: 'quoteId',
          header: 'Quote Id',
          cell: (props) => {
            return <BodyText>{formatQuoteId(props.row.original.quoteId)}</BodyText>
          },
        }),
      ]),
    [columnHelper, subaccount, cancelRfqQuote, mutateQuotes]
  )

  return (
    <Table
      {...tableProps}
      size="lg"
      data={rows}
      columns={columns}
      pageSize={pageSize}
      emptyMessage="No Quotes"
    />
  )
}

export default QuotesTable
